import styled from '@emotion/styled';
import { uniqueId } from '@lessonup/utils';
import { rem } from 'polished';
import React, { JSX, Ref, SelectHTMLAttributes } from 'react';
import { ComponentColors, ComponentSize, getComponentColors, spacing, spacingScaled } from '../../../foundations';
import { SpaceBetween } from '../../../utils/SpaceBetween/SpaceBetween';
import { IconChevronDown } from '../../icons';
import { InputFieldOrientation, SharedInputComponentProps } from '../input.types';
import {
  getInputFieldBorderAndColorStyles,
  getInputFieldFontStyles,
  inputFieldPaddingSize,
  inputLineHeightMap,
} from '../input.utils';
import { InputLabel } from '../InputLabel/InputLabel';
import { Validation } from '../Validation/Validation';

export type SelectValue = string | number | readonly string[] | undefined;
export interface SelectOption<V extends SelectValue> {
  label: string;
  value: V;
  disabled?: boolean;
}

/**
 * Props derived from the native HTML select element, excluding conflicting attributes like 'size', 'width', and 'value',
 * which are redefined by the component.
 */
export type NativeSelectProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'size' | 'width' | 'value' | 'defaultValue' | 'disabled'
>;

export interface SelectFieldProps<V extends SelectValue> extends NativeSelectProps, SharedInputComponentProps {
  ref?: Ref<HTMLSelectElement>;
  value?: V;
  defaultValue?: V;

  /**
   * The options to display in the select field.
   * Example:
   * ```tsx
   * const options = [
   *   { label: 'Option 1', value: 'option1' },
   *   { label: 'Option 2', value: 'option2', disabled: true },
   *   { label: 'Option 3', value: 'option3' },
   * ];
   * ```
   */
  options: SelectOption<V>[];

  /** Slot inside the field, left of the input. Meant for icons */
  iconSlot?: React.ReactNode;

  /** Slot that falls outside of the field */
  endSlot?: React.ReactNode;

  /** Whether the label is displayed above or to the left of the input */
  orientation?: InputFieldOrientation;
}

export const SelectField = <V extends SelectValue>(props: SelectFieldProps<V>): JSX.Element => {
  const {
    ref,
    id = uniqueId(`select-field-`),
    colorMode = 'neutral',
    size = 'small',
    className,
    validation,
    disabled,
    width,
    label,
    iconSlot,
    endSlot,
    orientation = 'vertical',
    // Remaining props to be passed to the native select element
    ...nativeSelectProps
  } = props;

  const colors = getComponentColors(colorMode, { disabled, noGradient: true });

  const validationId = `${id}-validation`;
  const describedById = validation?.message ? validationId : undefined;

  return (
    <StyledSelectFieldWrapper direction="y" spacing={spacing.size4} size={size} $width={width} className={className}>
      <SpaceBetween
        direction={orientation === 'horizontal' ? 'x' : 'y'}
        alignItems={orientation === 'horizontal' ? 'center' : undefined}
        spacing={orientation === 'horizontal' ? spacing.size8 : spacing.size2}
        size={size}
      >
        <InputLabel htmlFor={props.id} size={size}>
          {label}
        </InputLabel>
        <SpaceBetween direction="x" spacing={spacing.size4} size={size} alignItems="center">
          <StyledSelectWrapper colors={colors} $size={size}>
            <StyledIconSlotWrapper size={size} position="left">
              {iconSlot}
            </StyledIconSlotWrapper>
            <StyledSelect
              {...nativeSelectProps}
              ref={ref}
              disabled={disabled}
              id={id}
              colors={colors}
              $size={size}
              hasIconSlot={!!iconSlot}
              aria-label={props['aria-label'] || label}
              aria-describedby={describedById}
              aria-invalid={validation?.type === 'negative' && true}
            >
              {props.options.map((option, index) => (
                <option key={`${option.value}-${index}`} value={option.value} disabled={option.disabled}>
                  {option.label}
                </option>
              ))}
            </StyledSelect>
            <StyledIconSlotWrapper size={size} position="right">
              <IconChevronDown />
            </StyledIconSlotWrapper>
          </StyledSelectWrapper>
          {endSlot}
        </SpaceBetween>
      </SpaceBetween>
      {validation && <Validation id={validationId} size={size} colorMode={colorMode} {...validation} />}
    </StyledSelectFieldWrapper>
  );
};

const StyledSelectFieldWrapper = styled(SpaceBetween)<{ size: ComponentSize; $width?: number }>`
  width: ${({ $width }) => ($width ? rem($width) : 'auto')};
  position: relative;
`;

const StyledSelectWrapper = styled.div<{ colors: ComponentColors; $size: ComponentSize }>`
  ${({ colors, $size }) => getInputFieldBorderAndColorStyles(colors, $size)}
  position: relative;
  display: flex;
  align-items: center;
  flex-grow: 1;
  height: ${({ $size }) => rem(spacingScaled(spacing.size24, $size))};

  :hover {
    background-color: ${({ colors }) => colors.fillFeedback};
  }
`;

// Takes the chevron icon into account
const calculatePaddingRight = ($size: ComponentSize) =>
  `calc(${rem(spacingScaled(inputFieldPaddingSize, $size))} + ${rem(spacingScaled(spacing.size4, $size))} + ${inputLineHeightMap[$size]})`;

// Takes the icon slot into account
const calculatePaddingLeft = ($size: ComponentSize, hasIconSlot: boolean) =>
  `calc(${rem(spacingScaled(inputFieldPaddingSize, $size))} + ${rem(spacingScaled(spacing.size2, $size))} ${hasIconSlot ? ` + ${inputLineHeightMap[$size]} + ${rem(spacingScaled(spacing.size2, $size))}` : ''})`;

const StyledSelect = styled.select<{ colors: ComponentColors; $size: ComponentSize; hasIconSlot: boolean }>`
  ${({ $size }) => getInputFieldFontStyles($size)};
  overflow: hidden;
  width: 100%;
  appearance: none;
  border: none;
  background-color: transparent;
  color: inherit;

  padding-right: ${({ $size }) => calculatePaddingRight($size)};
  padding-left: ${({ $size, hasIconSlot }) => calculatePaddingLeft($size, hasIconSlot)};
  padding-top: ${({ $size }) => rem(spacingScaled(inputFieldPaddingSize, $size))};
  padding-bottom: ${({ $size }) => rem(spacingScaled(inputFieldPaddingSize, $size))};

  :focus {
    outline: none;
  }
`;

const StyledIconSlotWrapper = styled.div<{ size: ComponentSize; position: 'left' | 'right' }>`
  width: ${({ size }) => inputLineHeightMap[size]};
  height: ${({ size }) => inputLineHeightMap[size]};
  display: flex;
  position: absolute;
  left: ${({ position, size }) => (position === 'left' ? rem(spacingScaled(spacing.size4, size)) : 'auto')};
  right: ${({ position, size }) => (position === 'right' ? rem(spacingScaled(spacing.size4, size)) : 'auto')};
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  pointer-events: none;

  > *:first-child {
    width: 100%;
    height: 100%;
  }
`;
